import React from "react";
import {
  Award,
  Camera,
  ChevronRight,
  ExternalLink,
  Globe,
  type LucideIcon,
} from "lucide-react";
import { AppLink } from "../ui/link";
import { cn } from "../../lib/utils";

type HomeProductShortcutsProps = {
  jobsHref: string;
  credentialsHref: string;
  siteUrl?: string | null;
  billingRestricted?: boolean;
  onEditSite?: () => void;
  showWebsite?: boolean;
  showJobs?: boolean;
  showCredentials?: boolean;
};

const cardClass = "rounded-2xl border border-border bg-card shadow-sm";

const interactiveCardClass =
  "rounded-2xl border border-border bg-card shadow-sm transition-colors hover:border-premium-200 hover:bg-muted/40";

const rowClass =
  "flex items-center gap-3 px-4 py-3.5 text-foreground no-underline hover:no-underline";

type ProductRowProps = {
  label: string;
  href: string;
  icon: LucideIcon;
};

const ProductRow: React.FC<ProductRowProps> = ({ label, href, icon: Icon }) => (
  <AppLink
    to={href}
    variant="muted"
    className={cn(interactiveCardClass, rowClass)}
  >
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-premium-50 text-premium-600">
      <Icon className="h-4 w-4" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold">{label}</span>
    </span>
    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
  </AppLink>
);

export const HomeProductShortcuts: React.FC<HomeProductShortcutsProps> = ({
  jobsHref,
  credentialsHref,
  siteUrl,
  billingRestricted = false,
  onEditSite,
  showWebsite = true,
  showJobs = true,
  showCredentials = true,
}) => {
  const live = Boolean(siteUrl) && !billingRestricted;

  return (
    <nav aria-label="Products" className="mt-3 flex flex-col gap-2">
      {showWebsite ? (
        <div className={cardClass}>
          <div className={rowClass}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-premium-50 text-premium-600">
              <Globe className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold">Website</span>
            </span>
          </div>
          {siteUrl ? (
            <div
              className="flex items-center gap-2 border-t border-border px-4 py-2.5"
              data-tour="dashboard-site-url"
            >
              <span
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                  billingRestricted
                    ? "border-destructive/20 bg-destructive/10 text-destructive"
                    : "border-border bg-muted/60 text-muted-foreground",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    billingRestricted ? "bg-destructive" : "bg-success",
                  )}
                />
                {billingRestricted ? "Suspended" : live ? "Live" : "Site"}
              </span>
              <a
                href={siteUrl}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
              >
                {siteUrl.replace(/^https?:\/\//, "")}
              </a>
              {onEditSite ? (
                <button
                  type="button"
                  onClick={onEditSite}
                  className="shrink-0 rounded-md px-1.5 py-0.5 text-xs font-semibold text-premium-600 hover:bg-premium-50 hover:text-premium-700"
                >
                  Edit site
                </button>
              ) : (
                <a
                  href={siteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Open site"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {showJobs ? (
        <ProductRow label="Jobs" href={jobsHref} icon={Camera} />
      ) : null}
      {showCredentials ? (
        <ProductRow label="Credentials" href={credentialsHref} icon={Award} />
      ) : null}
    </nav>
  );
};
